import React, { useEffect } from 'react';
import { Icon } from './Icon.jsx';
import { fmtCountdown } from '../lib/session.js';

/* ── Session-expiry experience (v2.4) ───────────────────────────────
   The experience layer on top of the engineering baseline (401 → handler,
   re-check on tab focus). Five surfaces, all reassuring — never alarming:

     • SessionWarningBanner   — proactive, dismissible pre-expiry nudge
     • SessionExpiredModal    — blocking; reassures + preserves drafts
     • SessionNetworkModal    — disambiguates a genuine outage (NOT signed out)
     • ReauthOverlay          — full-page Google OAuth hand-off (no new tab)
     • SessionRestoredBanner  — "welcome back, we kept your work"

   Pixel-equivalent to the design prototype; copy is verbatim and final.
   ------------------------------------------------------------------- */

// Lock background scroll while a blocking modal is up. Returns scroll on
// unmount. Shared by the expired + network modals.
function useScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
}

/* Proactive nudge — appears a few minutes before the session lapses. Quiet
   apricot system banner, pinned below the bar, visible even over a full-screen
   import. One click ("Stay signed in") pings the server and resets the sliding
   window. */
export function SessionWarningBanner({ secondsLeft, onStay, onDismiss }) {
  const urgent = secondsLeft <= 60;
  return (
    <div className="session-banner" role="status" aria-live="polite">
      <div className="session-card session-card--warn">
        <span className="session-icon session-icon--warn">
          <Icon name="clock" size={18} stroke={2} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--sp-fg-1)',
              letterSpacing: '-0.005em',
            }}
          >
            You'll be signed out soon
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--sp-fg-2)',
              marginTop: 2,
              lineHeight: 1.45,
            }}
          >
            For security, ClockIn ends inactive sessions. Yours expires in{' '}
            <span className={'session-count' + (urgent ? ' is-urgent' : '')}>
              {fmtCountdown(secondsLeft)}
            </span>{' '}
            — one click keeps you going.
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: 'none',
          }}
        >
          <button
            className="btn btn-brand btn-sm"
            onClick={onStay}
            style={{ whiteSpace: 'nowrap' }}
          >
            Stay signed in
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onDismiss}
            aria-label="Dismiss session warning"
            title="Dismiss"
            style={{ width: 30, height: 30, color: 'var(--sp-fg-3)' }}
          >
            <Icon name="x" size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* The "kept your work" receipt shown inside the expired modal. */
function SavedWorkReceipt({ work }) {
  const meta =
    {
      jira: { icon: 'jiraMark', tint: '#E9F2FF', ink: '#0052CC', kind: 'Jira import' },
      calendar: { icon: 'cal', tint: 'var(--sp-mint-100)', ink: '#007C7C', kind: 'Calendar import' },
      copyday: { icon: 'copy', tint: 'var(--sp-mint-100)', ink: '#007C7C', kind: 'Copied day' },
      form: { icon: 'edit', tint: 'var(--sp-mint-100)', ink: '#007C7C', kind: 'Time entry' },
    }[work.kind] || { icon: 'edit', tint: 'var(--sp-mint-100)', ink: '#007C7C', kind: 'Draft' };

  return (
    <div className="session-receipt">
      <div className="session-receipt-head">
        <span
          className="session-icon session-icon--ok"
          style={{ width: 26, height: 26, borderRadius: 8 }}
        >
          <Icon name="check" size={14} stroke={2.6} />
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#026153' }}>
          Your work is safe
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--sp-fg-3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Icon name="lock" size={11} /> Saved on this device
        </span>
      </div>
      <div className="session-receipt-body">
        <span
          className="session-icon"
          style={{
            background: meta.tint,
            color: meta.ink,
            width: 36,
            height: 36,
            borderRadius: 10,
          }}
        >
          <Icon name={meta.icon} size={18} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
            {meta.kind}
            {work.rows > 1 ? ` · ${work.rows} rows in progress` : ' · draft in progress'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--sp-fg-3)', marginTop: 2 }}>
            {work.kind === 'jira' && (work.ready > 0 || work.incomplete > 0) ? (
              <>
                {work.ready} ready · {work.incomplete} still need details ·{' '}
                {work.dateLabel}
              </>
            ) : (
              <>For {work.dateLabel}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Blocking modal shown on expiry. Reassuring, not alarming. Content adapts to
   whether there is unsaved work to preserve. Renders above any open import
   overlay (backdrop z-index 350 > import 200). */
export function SessionExpiredModal({ work, user, onReauth }) {
  useScrollLock();
  return (
    <div className="session-backdrop">
      <div
        className="tt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        style={{ maxWidth: 460, marginTop: '12vh' }}
      >
        <div style={{ padding: '28px 28px 0', textAlign: 'center' }}>
          <div className="session-hero-icon">
            <Icon name="clock" size={26} stroke={2} />
          </div>
          <h2
            id="session-expired-title"
            style={{
              margin: '18px 0 8px',
              fontSize: 21,
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            Your session timed out
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: '22px',
              color: 'var(--sp-fg-2)',
            }}
          >
            You were signed out after 60 minutes of inactivity — that's how we
            keep your SwiftPay data secure.
          </p>
        </div>

        <div style={{ padding: '20px 28px 8px' }}>
          {work ? (
            <SavedWorkReceipt work={work} />
          ) : (
            <div className="session-receipt session-receipt--plain">
              <span
                className="session-icon session-icon--ok"
                style={{ width: 26, height: 26, borderRadius: 8 }}
              >
                <Icon name="check" size={14} stroke={2.6} />
              </span>
              <span style={{ fontSize: 13, color: 'var(--sp-fg-2)' }}>
                Nothing was in progress — you've lost nothing.
              </span>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 28px 28px' }}>
          <button onClick={onReauth} className="session-google-btn">
            <Icon name="google" size={18} /> Sign back in with Google
          </button>
          <div
            style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              fontSize: 12.5,
              color: 'var(--sp-fg-3)',
            }}
          >
            <Icon name="arrowR" size={13} />
            {work ? (
              <span>You'll come straight back here — nothing to redo.</span>
            ) : (
              <span>You'll return to exactly where you left off.</span>
            )}
          </div>
          <div
            style={{
              marginTop: 6,
              textAlign: 'center',
              fontSize: 11.5,
              color: 'var(--sp-fg-4)',
            }}
          >
            Signed in as {user?.email}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Disambiguation: a genuine network/server failure is NOT a session expiry.
   Different colour, different icon, different action (retry, not re-auth) —
   and it explicitly reassures the session is still alive. Dismissible by
   backdrop click (unlike the expired modal). */
export function SessionNetworkModal({ onRetry, onDismiss, retrying }) {
  useScrollLock();
  return (
    <div
      className="session-backdrop"
      onClick={retrying ? undefined : onDismiss}
    >
      <div
        className="tt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-network-title"
        style={{ maxWidth: 440, marginTop: '14vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '28px 28px 8px', textAlign: 'center' }}>
          <div className="session-hero-icon session-hero-icon--warn">
            <Icon name="alert" size={24} stroke={2} />
          </div>
          <h2
            id="session-network-title"
            style={{
              margin: '18px 0 8px',
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: '-0.005em',
            }}
          >
            Can't reach ClockIn
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: '22px',
              color: 'var(--sp-fg-2)',
            }}
          >
            This looks like a network hiccup, not a sign-out.{' '}
            <strong>
              Your session is still active and your work is safe
            </strong>{' '}
            — we'll keep retrying.
          </p>
        </div>
        <div
          style={{
            padding: '20px 28px 26px',
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
          }}
        >
          <button className="btn btn-ghost" onClick={onDismiss} disabled={retrying}>
            Dismiss
          </button>
          <button
            className="btn btn-primary"
            onClick={onRetry}
            disabled={retrying}
            style={{ minWidth: 132 }}
          >
            {retrying && <span className="tt-spinner sm" style={{ marginRight: 6 }} />}
            {retrying ? 'Retrying…' : 'Retry now'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Full-page Google OAuth hand-off. Honours the hard constraint that re-auth is
   a full redirect (no inline password, no new tab) — but because the draft is
   persisted first, the redirect is safe and the return is seamless. Two stages:
   'redirecting' (before window.location → Google) and 'returning' (the brief
   post-OAuth re-bootstrap while the draft rehydrates). */
export function ReauthOverlay({ stage, user, work }) {
  const returning = stage === 'returning';
  return (
    <div className="session-overlay">
      <div
        style={{
          position: 'absolute',
          top: 22,
          left: 28,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <img src="/swiftpay/SwiftPay-logo-black.svg" style={{ height: 20 }} alt="SwiftPay" />
        <span className="tt-product-tag">ClockIn</span>
      </div>

      <div style={{ textAlign: 'center', maxWidth: 360, padding: '0 24px' }}>
        <div className={'session-reauth-mark' + (returning ? ' is-returning' : '')}>
          {returning ? (
            <Icon name="check" size={30} stroke={2.4} />
          ) : (
            <Icon name="google" size={34} />
          )}
        </div>
        <h2
          style={{
            margin: '24px 0 8px',
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: '-0.005em',
          }}
        >
          {returning ? 'Signing you back in…' : 'Connecting to Google'}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: '22px',
            color: 'var(--sp-fg-2)',
          }}
        >
          {returning ? (
            work ? (
              <>
                Restoring your {work.kind === 'jira' ? 'Jira import' : 'draft'} —{' '}
                {work.rows > 1 ? `${work.rows} rows` : 'one entry'} on the way back.
              </>
            ) : (
              <>Taking you back to where you left off.</>
            )
          ) : (
            <>
              Re-authenticating <strong>{user?.email}</strong> with your SwiftPay
              workspace.
            </>
          )}
        </p>
        <div
          style={{
            marginTop: 22,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 9,
            color: 'var(--sp-fg-3)',
            fontSize: 13,
          }}
        >
          {!returning && <span className="tt-spinner sm" />}
          {returning ? 'Almost there' : 'Redirecting…'}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 26,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--sp-fg-4)',
        }}
      >
        Secure sign-in · accounts.google.com
      </div>
    </div>
  );
}

/* Post-return confirmation. Floats above any surface (incl. a reopened import)
   so the user sees their work was preserved. Auto-dismisses (the caller owns
   the timer). */
export function SessionRestoredBanner({ work, user, onDismiss }) {
  const first = (user?.name || '').trim().split(/\s+/)[0] || 'there';
  return (
    <div className="session-banner" role="status" aria-live="polite">
      <div className="session-card session-card--ok">
        <span className="session-icon session-icon--ok">
          <Icon name="check" size={18} stroke={2.6} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--sp-fg-1)',
              letterSpacing: '-0.005em',
            }}
          >
            Welcome back, {first} — you're signed in again
          </div>
          <div style={{ fontSize: 13, color: 'var(--sp-fg-2)', marginTop: 2 }}>
            {work ? (
              <>
                We picked up right where you left off. Your{' '}
                {work.kind === 'jira' ? 'Jira import' : 'draft'} was restored
                {work.rows > 1 ? ` — all ${work.rows} rows.` : '.'}
              </>
            ) : (
              <>We picked up right where you left off.</>
            )}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-icon"
          onClick={onDismiss}
          aria-label="Dismiss welcome-back message"
          title="Dismiss"
          style={{ width: 30, height: 30, color: 'var(--sp-fg-3)', flex: 'none' }}
        >
          <Icon name="x" size={15} />
        </button>
      </div>
    </div>
  );
}
